import { formatDate, formatDateTime } from '../imports/importState'

// Every field of the crew card and of the data around it (family, contacts, documents, climbs...),
// grouped as in the import workbook. WFR certificates and the seniority bonus are not shown.
// `sortable` columns can be ordered; `sortKey` is the name the API knows the order by.
// `type` decides how a plain cell is formatted; the columns with a `slot` are drawn by Crew.vue.

const EMPTY = '—'

const first = (list) => (list && list.length ? list[0] : null)
const nested = (name) => (row) => (row[name] ? row[name].name : null)
const person = (name) => (row) => (row[name] ? row[name].full_name : null)
const contact = (index, field) => (row) => {
  const item = (row.emergency_contacts || [])[index]
  if (!item) return null
  return field === 'relationship' ? (item.relationship ? item.relationship.name : null) : item[field]
}
const baseline = (field) => (row) => (row.climb_baseline ? row.climb_baseline[field] : null)
const climbYear = (year) => (row) => (row.climb_baseline ? row.climb_baseline.years[year] : null)
const porter = (field) => (row) => (row.porter_document ? row.porter_document[field] : null)
const language = (field) => (row) => {
  const item = first(row.spoken_languages)
  if (!item) return null
  if (field === 'name') {
    const more = row.spoken_languages.length - 1
    return more > 0 ? `${item.language.name} +${more}` : item.language.name
  }
  if (field === 'level') return item.level ? item.level.name : null
  return item.test_date
}
const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : null)

function column(key, label, width, options = {}) {
  return { key, label, width: `${width}px`, ...options }
}

// Frozen columns stick side by side: each one is offset by the widths of the frozen ones before it.
function withFrozenOffsets(columns) {
  let offset = 0
  columns.forEach(item => {
    if (!item.sticky) return
    if (offset) item.stickyLeft = `${offset}px`
    offset += parseInt(item.width, 10)
  })
  return columns
}

export const CREW_COLUMNS = withFrozenOffsets([
  // identity: External ID (the "#" of the registry, the key of the import) and Name are frozen, so every
  // row stays identifiable while the grid scrolls sideways. The database id is not shown.
  column('external_id', 'External ID', 120, { sticky: true, sortable: true, sortKey: 'external_id' }),
  column('name', 'Name', 220, { slot: true, sticky: true }),
  column('old_id', 'Old #', 110),
  column('first_name', 'First name', 140),
  column('middle_name', 'Middle name', 140),
  column('last_name', 'Last name', 140),
  column('gender', 'Gender', 100, { get: row => capitalize(row.gender) }),
  column('dob', 'Date of birth', 130, { type: 'date' }),
  column('age', 'Age', 70, { type: 'number', align: 'right' }),
  // registration
  column('registered_on', 'Registered on', 130, { type: 'date' }),
  column('registered_by_name', 'Registered by', 160),
  // positions, tags, place
  column('positions', 'Positions', 280, { slot: true }),
  column('tags', 'Tags', 220, { slot: true }),
  column('residence_text', 'Current address', 170),
  column('region', 'Region', 140, { get: nested('region') }),
  // rank
  column('rank', 'Rank', 130, { slot: true }),
  column('rank_assigned_on', 'Rank assigned on', 140, { type: 'date' }),
  column('rank_assigned_by_name', 'Rank assigned by', 160),
  // languages
  column('language', 'Extra language', 150, { get: language('name') }),
  column('language_level', 'Language level', 130, { get: language('level') }),
  column('language_test_date', 'Language test date', 150, { type: 'date', get: language('test_date') }),
  // reference
  column('reference', 'Reference contact', 220, { get: row => (row.reference ? row.reference.raw_text : null) }),
  // phones and messengers
  column('phone', 'Main phone', 150),
  column('phone_telegram_available', 'Main phone: Telegram', 170, { type: 'bool' }),
  column('phone_whatsapp_available', 'Main phone: WhatsApp', 170, { type: 'bool' }),
  column('phone_carrier', 'Main phone carrier', 150, { get: nested('phone_carrier') }),
  column('telegram_id', 'Telegram ID', 140),
  column('telegram_username', 'Telegram username', 160),
  column('additional_phone', 'Additional phone', 150),
  column('additional_phone_telegram_available', 'Add. phone: Telegram', 170, { type: 'bool' }),
  column('additional_phone_whatsapp_available', 'Add. phone: WhatsApp', 170, { type: 'bool' }),
  column('additional_phone_carrier', 'Add. phone carrier', 150, { get: nested('additional_phone_carrier') }),
  column('telegram_id_secondary', 'Second Telegram ID', 160),
  // family and emergency contacts
  column('mother_name', 'Mother', 200),
  column('father_name', 'Father', 200),
  column('ec1_name', 'Emergency contact 1', 200, { get: contact(0, 'full_name') }),
  column('ec1_relationship', 'EC 1 relationship', 150, { get: contact(0, 'relationship') }),
  column('ec1_phone', 'EC 1 phone', 150, { get: contact(0, 'phone') }),
  column('ec2_name', 'Emergency contact 2', 200, { get: contact(1, 'full_name') }),
  column('ec2_relationship', 'EC 2 relationship', 150, { get: contact(1, 'relationship') }),
  column('ec2_phone', 'EC 2 phone', 150, { get: contact(1, 'phone') }),
  // documents
  column('national_id', 'National ID', 190),
  column('voting_card', 'Voting card', 160),
  column('tin', 'TIN', 130),
  column('birth_certificate', 'Birth certificate', 150, { type: 'bool', get: row => Boolean(row.birth_certificate) }),
  column('guiding_license', 'Guide licence', 140),
  column('guiding_license_issued', 'Guide licence issued', 160, { type: 'date' }),
  column('guiding_license_expire', 'Guide licence valid until', 190, { type: 'date' }),
  column('porter_number', 'Porter ID', 150, { get: porter('number') }),
  column('porter_association', 'Porter ID association', 170, { get: porter('association') }),
  column('porter_issued_on', 'Porter ID issued', 150, { type: 'date', get: porter('issued_on') }),
  column('porter_valid_until', 'Porter ID valid until', 170, { type: 'date', get: porter('valid_until') }),
  column('age_permit_expire', 'Age permit expires', 160, { type: 'date' }),
  // bank
  column('bank_account', 'Bank account', 160),
  column('bank_account_name', 'Account name', 180),
  // restrictions and notes
  column('blocked_date', 'Blocked date', 130, { type: 'date' }),
  column('hold_date', 'On hold since', 130, { type: 'date' }),
  column('next_evaluation_on', 'Next evaluation', 150, { type: 'date' }),
  column('important_records', 'Important records', 260),
  // climbs
  column('climb_total', 'Total climbs', 120, { type: 'number', align: 'right', get: baseline('reported_total') }),
  ...[2025, 2024, 2023, 2022, 2021, 2020, 2019].map(year => (
    column(`climbs_${year}`, `Climbs ${year}`, 110, { type: 'number', align: 'right', get: climbYear(year) })
  )),
  column('last_climb', 'Last climb', 130, { type: 'date', get: baseline('last_climb') }),
  // history
  column('old_id_aliases', 'Duplicates Ref', 150, { get: row => (row.old_id_aliases || []).join(', ') }),
  column('role', 'Role (legacy)', 130, { get: nested('role') }),
  column('guarantor_crew', 'Guarantor', 180, { get: person('guarantor_crew') }),
  column('guarantor_other', 'Guarantor (other)', 170),
  // import and audit
  column('registry', 'Latest import', 170, { slot: true, sortable: true, sortKey: 'latest_import' }),
  column('created_at', 'Created', 170, { type: 'datetime', sortable: true, sortKey: 'created_at' }),
  column('created_by', 'Created by', 160, { get: person('created_by') }),
  column('updated_at', 'Updated', 170, { type: 'datetime', sortable: true, sortKey: 'updated_at' }),
  column('updated_by', 'Updated by', 160, { get: person('updated_by') })
])

export const CREW_TABLE_MIN_WIDTH = `${CREW_COLUMNS.reduce((sum, item) => sum + parseInt(item.width, 10), 0)}px`

export function cellText(row, column) {
  const value = column.get ? column.get(row) : row[column.key]
  if (column.type === 'date') return formatDate(value)
  if (column.type === 'datetime') return formatDateTime(value)
  if (column.type === 'bool') return value === null || value === undefined ? EMPTY : (value ? 'Yes' : 'No')
  if (column.type === 'number') return value === null || value === undefined ? EMPTY : String(value)
  return value === null || value === undefined || value === '' ? EMPTY : String(value)
}

// The `ordering` query parameter for the table's current sort: "-old_id" for descending, undefined
// for the default order (and for anything that is not a sortable column).
export function orderingParam(sort) {
  if (!sort) return undefined
  const column = CREW_COLUMNS.find(item => item.key === sort.key && item.sortable)
  return column ? `${sort.direction === 'desc' ? '-' : ''}${column.sortKey}` : undefined
}
